// The code Get started and Settings show, read from the docs pages at build time (docs/site, reduced to their
// headings and code by vite.config.ts), so the console and the docs show the same snippets: one
// source. A snippet is a fenced block of a page's section, found by its heading; the app key
// replaces the docs' placeholder `a-…`.
import androidSdk from '../../../../docs/site/sdks/android.mdx?blocks'
import iosSdk from '../../../../docs/site/sdks/ios.mdx?blocks'
import rnSdk from '../../../../docs/site/sdks/react-native.mdx?blocks'
import androidSymbols from '../../../../docs/site/symbols/android.mdx?blocks'
import ndkSymbols from '../../../../docs/site/symbols/android-ndk.mdx?blocks'
import ciSymbols from '../../../../docs/site/symbols/ci.mdx?blocks'
import rnSymbols from '../../../../docs/site/symbols/react-native.mdx?blocks'
import uploadSymbols from '../../../../docs/site/symbols/upload.mdx?blocks'
import xcodeSymbols from '../../../../docs/site/symbols/xcode.mdx?blocks'
import type { Platform } from '../api/types'

export const DOCS = 'https://docs.apsio.io'

export interface CodeBlock {
  lang: string
  code: string
}

/**
 * The fenced blocks of a page's section: from the heading to the next heading of the same or a
 * higher level. Indented blocks (inside a list item) are dedented.
 */
export function sectionBlocks(page: string, heading: string): CodeBlock[] {
  const lines = page.split('\n')
  const start = lines.findIndex((l) => /^#{2,6} /.test(l) && l.replace(/^#+ /, '') === heading)
  if (start < 0) return []
  const level = (lines[start]?.match(/^#+/)?.[0].length ?? 2) as number
  const blocks: CodeBlock[] = []
  let open: { lang: string; indent: string; body: string[] } | undefined
  for (const line of lines.slice(start + 1)) {
    if (open) {
      if (line.trim() === '```') {
        blocks.push({ lang: open.lang, code: open.body.join('\n') })
        open = undefined
      } else {
        open.body.push(line.startsWith(open.indent) ? line.slice(open.indent.length) : line)
      }
      continue
    }
    const h = line.match(/^(#+) /)
    if (h && (h[1]?.length ?? 0) <= level) break
    const fence = line.match(/^(\s*)```(\S*)/)
    if (fence) open = { lang: fence[2] ?? '', indent: fence[1] ?? '', body: [] }
  }
  return blocks
}

const block = (page: string, heading: string, pick: (b: CodeBlock) => boolean): string => {
  const found = sectionBlocks(page, heading).find(pick)
  if (!found) throw new Error(`docs: no code block under "${heading}"`)
  return found.code
}
const any = () => true

/** A snippet for a code window's tab: its label, the code, and a docs page for the rest. */
export interface DocSnippet {
  id: string
  label: string
  code: string
  docs: string
  /** What Copy takes, when not the whole code: the one command to run. */
  copy?: string
}

const START: Record<'swiftui' | 'uikit' | 'android' | 'rn', Omit<DocSnippet, 'id'>> = {
  swiftui: {
    label: 'SwiftUI',
    code: block(iosSdk, 'Start', (b) => b.code.includes('@main')),
    docs: `${DOCS}/sdks/ios`,
  },
  uikit: {
    label: 'UIKit',
    code: block(iosSdk, 'Start', (b) => b.code.includes('UIApplicationDelegate')),
    docs: `${DOCS}/sdks/ios`,
  },
  android: {
    label: 'Android',
    code: [
      block(androidSdk, 'Install', (b) =>
        b.code.includes('implementation("io.apsio:apsio-android'),
      ),
      block(androidSdk, 'Start', any),
    ].join('\n\n'),
    docs: `${DOCS}/sdks/android`,
  },
  rn: {
    label: 'React Native',
    code: block(rnSdk, 'Start', (b) => b.lang === 'ts'),
    docs: `${DOCS}/sdks/react-native`,
  },
}

/** The placeholder the docs write for an app key. */
export const KEY_PLACEHOLDER = 'a-…'

/** The comment the docs put beside the key, which a page that shows the key does not need. */
const KEY_COMMENT = / +\/\/ your app key, from Settings [›→] .+$/m

/**
 * The start snippets, the app's platform first, with its key in place of the placeholder: the
 * docs' comment beside the key goes, as the key is right there (APS-410).
 */
export function startTabs(platform: Platform, key: string): DocSnippet[] {
  const order: (keyof typeof START)[] =
    platform === 'android'
      ? ['android', 'swiftui', 'uikit', 'rn']
      : platform === 'react_native'
        ? ['rn', 'swiftui', 'uikit', 'android']
        : ['swiftui', 'uikit', 'android', 'rn']
  return order.map((id) => ({
    id,
    ...START[id],
    code: START[id].code.replaceAll(KEY_PLACEHOLDER, key).replace(KEY_COMMENT, ''),
  }))
}

/** The iOS package step, as Xcode asks for it. */
const IOS_PACKAGE = `// File › Add Package Dependencies, branch main\n${block(iosSdk, 'Install', (b) => b.code.startsWith('https://'))}`

/**
 * The start snippets of the platform's own SDK alone (iOS: SwiftUI, then UIKit, each after the
 * package step), for a page that shows the app's key.
 */
export function ownStartTabs(platform: Platform, key: string): DocSnippet[] {
  const own = START_OF[platform]
  return startTabs(platform, key)
    .filter((x) => own.includes(x.id as keyof typeof START))
    .map((x) => ({ ...x, code: platform === 'ios' ? `${IOS_PACKAGE}\n\n${x.code}` : x.code }))
}
const START_OF: Record<Platform, (keyof typeof START)[]> = {
  ios: ['swiftui', 'uikit'],
  android: ['android'],
  react_native: ['rn'],
}

const SYMBOLS: Record<Platform, DocSnippet[]> = {
  ios: [
    {
      id: 'actions',
      label: 'GitHub Actions',
      code: block(ciSymbols, 'GitHub Actions', any),
      docs: `${DOCS}/symbols/ci`,
    },
    {
      id: 'archive',
      label: 'After an archive',
      code: block(ciSymbols, 'After an archive', any),
      docs: `${DOCS}/symbols/ci`,
    },
    {
      id: 'xcode',
      label: 'Xcode build phase',
      code: block(xcodeSymbols, 'Set it up', (b) => b.lang === 'sh'),
      docs: `${DOCS}/symbols/xcode`,
    },
  ],
  android: [
    {
      id: 'gradle',
      label: 'Gradle plugin',
      code: block(androidSdk, 'Symbols', (b) => b.lang === 'kotlin'),
      docs: `${DOCS}/sdks/android#symbols`,
    },
  ],
  react_native: [
    {
      id: 'sourcemaps',
      label: 'Source maps',
      code: block(rnSymbols, 'Upload it after the build', any),
      docs: `${DOCS}/symbols/react-native`,
    },
  ],
}

/** The ways to upload symbols from a build, for the platforms given, in that order. */
export function symbolTabs(platforms: readonly Platform[]): DocSnippet[] {
  return [...new Set(platforms)].flatMap((p) => SYMBOLS[p])
}

/** A symbol file a crash needs, as Settings › Symbols names them (APS-319). */
export type SymbolFile = 'dsym' | 'mapping' | 'ndk' | 'sourcemap'

const firstCommand = (code: string): string =>
  code.split('\n').find((l) => l.startsWith('apsio ')) ?? code

const mappingUpload = block(androidSymbols, '2. Upload the mapping after the build', any)
const ndkUpload = block(ndkSymbols, 'With the CLI', any)
const sourcemapUpload = block(rnSymbols, 'Upload it after the build', any)
const dsymUpload = block(uploadSymbols, 'Upload', any)

const FILES: Record<SymbolFile, DocSnippet> = {
  dsym: {
    id: 'dsym',
    label: 'dSYM',
    code: dsymUpload,
    copy: firstCommand(dsymUpload),
    docs: `${DOCS}/symbols/upload`,
  },
  mapping: {
    id: 'mapping',
    label: 'R8 mapping',
    code: [
      block(androidSdk, 'Symbols', (b) => b.lang === 'kotlin'),
      block(androidSymbols, '1. Give the build an id', (b) => b.lang === 'sh'),
      mappingUpload,
    ].join('\n\n'),
    copy: mappingUpload,
    docs: `${DOCS}/symbols/android`,
  },
  ndk: {
    id: 'ndk',
    label: 'NDK',
    code: [block(ndkSymbols, 'With the Gradle plugin', any), ndkUpload].join('\n\n'),
    copy: ndkUpload,
    docs: `${DOCS}/symbols/android-ndk`,
  },
  sourcemap: {
    id: 'sourcemap',
    label: 'Source maps',
    code: sourcemapUpload,
    copy: sourcemapUpload,
    docs: `${DOCS}/symbols/react-native`,
  },
}

/**
 * One tab per symbol file the platforms need, every one with no platform yet. React Native
 * apps need the source maps and the native files of both platforms.
 */
export function symbolFileTabs(platforms: ReadonlySet<Platform>): DocSnippet[] {
  const all = platforms.size === 0 || platforms.has('react_native')
  const ios = all || platforms.has('ios')
  const android = all || platforms.has('android')
  return [
    ...(ios ? [FILES.dsym] : []),
    ...(android ? [FILES.mapping, FILES.ndk] : []),
    FILES.sourcemap,
  ]
}
